import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useParams } from "@Libs/Router/hooks/useParams";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";

import { serviceRequestPathFragment } from "../../config/routes";
import { AUTH_SCREEN_KEYS, isAuthBranchScreenKey } from "../config/constants";
import { useTaskCreationFlow } from "../Contexts/TaskCreationFlow";
import { useDraftIdentity } from "./useDraftIdentity";
import { useFlowCategoryParams } from "./useFlowPCategory";

/**
 * Whether "back" out of the current screen leaves the flow, plus the exit
 * itself. Extracted from `useFlowBack` so the decision and navigation remain
 * in one place.
 */
export const useFlowLeaveGuard = () => {
  const navigate = useNavigate();
  const { screens, isFlowLoading } = useTaskCreationFlow();
  // The URL's own key, not the flow's `currentScreenKey`: that one is derived
  // from the flow shape, so it reads `undefined` whenever the shape is not
  // known yet (the draft or the config still in flight) or does not carry the
  // key at all. Every consumer then read "unknown" as "nothing before this
  // screen", i.e. as a reason to leave the flow — so a back press landing in
  // that window threw the visitor out to the Rails category picker from any
  // depth, losing `p_category`/`s_category` with it (RT-4321). On an auth
  // branch it also reports the email screen, which is exactly what this has to
  // tell apart from its branches.
  const { isEditing, taskId, screenFragment } = useDraftIdentity();
  const { screenKey } = useParams([screenFragment]);
  const categoryParams = useFlowCategoryParams();
  // Only a SEM landing sends this; the category picker sends `p_category`.
  //
  // The URL alone, never the draft's own `s_category_id`: the draft holds one
  // as soon as *any* visitor picks a subcategory, so falling back to it
  // stamped the SEM marker onto an ordinary visitor's description URL the
  // moment they stepped back — and the description screen reads that marker
  // as "this visitor already told us their subcategory", so its next submit
  // skipped the picker entirely (RT-4321). A real SEM visitor's URL carries
  // the param on every screen (`ServiceRequestCreationRoot`'s skip and the
  // description screen's own submit both forward it), so the URL is the
  // complete signal.
  const sCategory = useSearchParams("s_category");

  const index = screens.findIndex((screen) => screen.key === screenKey);
  const previous = index > 0 ? screens[index - 1] : undefined;
  // The key is not in the shape *yet* — a hard refresh or a resumed draft mid
  // fetch. There is no answer to give, so back sits still and the visitor can
  // press it again once the flow has loaded, rather than being dropped out of
  // it. An unknown key with the shape already loaded (a stale bookmark, an
  // edited URL) is a real dead end and does leave, below.
  const isPending = index === -1 && isFlowLoading;

  return {
    previous,
    /** True while back has no answer yet — the caller must do nothing. */
    isPending,
    /**
     * True when back would leave the flow instead of stepping inside it. An
     * auth branch never does — it steps back to the email screen it hangs off,
     * even though it is as absent from the shape as the picker's `previous`.
     */
    leavesFlow: !isAuthBranchScreenKey(screenKey) && !previous && !isPending,
    requestLeave: () =>
      // Editing steps back onto its own description screen, which is a screen
      // of the edit flow rather than a way out of it — nothing to resume, and
      // no create-flow params to carry.
      isEditing
        ? navigate(serviceRequestPathFragment.SERVICE_REQUEST_EDIT, {
            params: { id: String(taskId) },
          })
        : navigate(serviceRequestPathFragment.SERVICE_REQUEST_CREATION, {
            withLocale: true,
            queryParams: {
              ...categoryParams,
              ...(sCategory ? { s_category: sCategory } : {}),
              // Tells the description screen it is being stepped back into,
              // not arrived at fresh — it prefills from the draft instead of
              // offering to resume or discard it (RT-4321).
              from_step: "1",
            },
          }),
  };
};

/**
 * Where "back" goes from the screen currently on show (RT-4321).
 *
 * One hook rather than a handler per screen, because two places need the same
 * answer: the Retour button in the screen's own CTA row, and the back link the
 * shell renders above the summary panel on desktop.
 *
 * Three destinations. A screen with one before it in the flow shape goes
 * there — including a SEM entry's first config screen: the picker is still
 * `screens[0]` even though that visitor was redirected past it, so its
 * "previous" is the picker, not the description screen, and Back lands them
 * there rather than skipping it a second time (RT-4321).
 *
 * Only the picker itself has nothing before it, so *that* is what leaves the
 * flow entirely — straight back to the description screen, no confirmation
 * and no clearing the draft (RT-4321). Moving between two screens of the flow
 * stays instant, and carries the theme along so no hop drops it.
 *
 * The third is the auth step's branches: they hang off its email screen rather
 * than sitting beside it in the shape, so they name it explicitly.
 */
export const useFlowBack = () => {
  const navigate = useNavigate();
  const categoryParams = useFlowCategoryParams();
  const { screenRoute } = useDraftIdentity();
  const { previous, isPending, leavesFlow, requestLeave } = useFlowLeaveGuard();
  const { screenKey } = useParams([
    serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN,
  ]);

  return () => {
    // A branch names its destination rather than taking `previous`: it is not
    // in the flow shape, so the shape has no answer for it — and no answer is
    // needed, which is why this runs before the `isPending` wait.
    if (isAuthBranchScreenKey(screenKey))
      return navigate(
        serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN,
        {
          withLocale: true,
          params: { screenKey: AUTH_SCREEN_KEYS.EMAIL },
          queryParams: categoryParams,
        }
      );

    if (isPending) return;
    if (leavesFlow || !previous) return requestLeave();

    return navigate(screenRoute(previous.key).fragments, {
      withLocale: true,
      params: screenRoute(previous.key).params,
      queryParams: categoryParams,
    });
  };
};

/**
 * Where the description screen's own Back goes — the category picker is the
 * only place any entry to this screen came from (RT-4321).
 *
 * Kept beside `useFlowBack` because both are the flow's explicit Back actions.
 */
export const useDescriptionBack = () => {
  const navigate = useNavigate();
  const { isEditing, taskId } = useDraftIdentity();

  // Editing came from the request's own page, not from the picker.
  return () =>
    isEditing
      ? navigate(serviceRequestPathFragment.SERVICE_REQUEST, {
          reload: true,
          params: { id: String(taskId) },
        })
      : navigate("REQUEST_SERVICE", { withLocale: true, reload: true });
};
